import React, { useEffect, useState } from 'react';
import { getCsrfToken } from '../lib/csrf.js';

const labelClass = "block text-sm font-semibold text-gray-800";
const inputClass = "mt-1.5 block w-full rounded-lg border-0 bg-white px-3.5 py-2.5 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 transition duration-150 hover:ring-gray-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600";
const primaryBtnClass = "rounded-lg bg-indigo-600 px-5 py-2.5 text-sm/6 font-semibold text-white shadow-sm ring-1 ring-inset ring-indigo-600/20 transition duration-150 hover:bg-indigo-700 hover:shadow-md cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none";
const secondaryBtnClass = "rounded-lg bg-white px-5 py-2.5 text-sm/6 font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-200 transition duration-150 hover:bg-gray-50 hover:ring-gray-300 cursor-pointer";

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function CloseIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

function ChatIcon({ className = "h-6 w-6" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>
  );
}

function UserIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
    </svg>
  );
}

function MailIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
    </svg>
  );
}

function BuildingIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
    </svg>
  );
}

function UsersIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
    </svg>
  );
}

function PhoneIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.733.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
    </svg>
  );
}

function ClockIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );
}

function CheckCircleIcon({ className = "h-6 w-6" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );
}

function SummaryRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <span className="text-indigo-500">{icon}</span>
      <span className="text-xs font-medium text-gray-500 min-w-[60px]">{label}</span>
      <span className="text-xs text-gray-900 font-medium truncate">{value}</span>
    </div>
  );
}

export default function ContactSalesModal({ open, onClose, buyer, facility, totalSeats, seatsBreakdown }) {
  const [phone, setPhone] = useState('');
  const [additionalInformation, setAdditionalInformation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setPhone('');
      setAdditionalInformation('');
      setError(null);
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const canSubmit = phone.replace(/\D/g, '').length === 10 && !submitting;
  const buyerName = [buyer?.first_name, buyer?.last_name].filter(Boolean).join(' ').trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    try {
      const csrfToken = getCsrfToken();
      const res = await fetch('/api/sales_inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          buyer,
          facility,
          phone,
          additional_information: additionalInformation,
          total_seats: totalSeats,
          seats_breakdown: seatsBreakdown,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.error || 'Could not submit. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative w-full sm:max-w-lg overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl animate-step-in">
        <div className="relative bg-gradient-to-br from-indigo-600 via-indigo-600 to-violet-600 px-6 pt-6 pb-7 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-md p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <CloseIcon />
          </button>
          <h2 className="text-center text-xl font-bold tracking-tight">Talk to our sales team</h2>
        </div>

        {success ? (
          <div className="px-6 pb-6 pt-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                <CheckCircleIcon className="h-8 w-8" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-gray-900">Request received</h3>
              <p className="mt-1.5 text-sm text-gray-600">
                Thanks{buyerName ? `, ${buyer.first_name}` : ''}. A confirmation has been sent to{' '}
                <span className="font-medium text-gray-900">{buyer?.email || 'your email'}</span>.
              </p>
              <p className="mt-1 text-xs text-gray-500">A team member will be in touch soon.</p>
            </div>
            <div className="mt-6 flex justify-center">
              <button type="button" onClick={onClose} className={primaryBtnClass}>Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 pb-6 pt-5 space-y-4">
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
            )}

            <div className="rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white p-4 shadow-sm">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Your request details</p>
              <div className="divide-y divide-gray-100">
                {buyerName && <SummaryRow icon={<UserIcon />} label="Name" value={buyerName} />}
                {buyer?.email && <SummaryRow icon={<MailIcon />} label="Email" value={buyer.email} />}
                {facility?.name && <SummaryRow icon={<BuildingIcon />} label="Facility" value={facility.name} />}
                {totalSeats > 0 && <SummaryRow icon={<UsersIcon />} label="Seats" value={`${totalSeats} ${totalSeats === 1 ? 'seat' : 'seats'}`} />}
              </div>
            </div>

            <div>
              <label className={labelClass}>Phone Number <span className="text-red-500">*</span></label>
              <div className="relative mt-1.5">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
                  <PhoneIcon />
                </span>
                <input
                  type="tel"
                  inputMode="tel"
                  className={`${inputClass} mt-0 pl-10`}
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="(555) 555-1234"
                  required
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Additional Information <span className="text-gray-400 font-normal">(Optional)</span></label>
              <textarea
                className={`${inputClass} min-h-[110px] resize-y`}
                value={additionalInformation}
                onChange={(e) => setAdditionalInformation(e.target.value)}
                placeholder="Anything else we should know? (timing, special requirements, etc.)"
              />
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <button type="submit" disabled={!canSubmit} className={`${primaryBtnClass} w-full`}>
                {submitting ? 'Sending…' : 'Send'}
              </button>
              <button type="button" onClick={onClose} className={`${secondaryBtnClass} w-full`}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
